import {
  faCaretDown,
  faExclamationTriangle,
  faPlusCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { RootState } from '../redux/store'
import type { CurrencyDto, WalletDto } from '../types/coreApi-types'
import { isAdmin } from '../utils/roleUtils'
import AddWalletModal from './AddWalletModal'
import DropdownCell from './DropdownCell'
import DropdownMenu from './DropdownMenu'
import Icon from './Icon'
import Tooltip from './Tooltip'

interface WalletSelectorProps {
  label: string
  currency?: CurrencyDto
  wallet: WalletDto | null
  setWallet: (wallet: WalletDto | null) => void
  walletsData: WalletDto[] | undefined
  isWalletAndCryptoCurrencyPairCompatible: (
    wallet: WalletDto,
    currency?: CurrencyDto
  ) => boolean
  disabled?: boolean
  showAddWalletOption?: boolean
  className?: string
  history?: any
}

const WalletSelector: React.FC<WalletSelectorProps> = ({
  label,
  currency,
  wallet,
  setWallet,
  walletsData,
  isWalletAndCryptoCurrencyPairCompatible,
  disabled = false,
  showAddWalletOption = true,
  className = '',
  history: propHistory,
}) => {
  const defaultHistory = useHistory()
  const history = propHistory || defaultHistory
  const userInfo = useSelector((state: RootState) => state.session.user)
  const [isAddWalletModalOpen, setIsAddWalletModalOpen] = useState(false)

  const handleAddWalletClick = () => {
    if (showAddWalletOption) {
      setIsAddWalletModalOpen(true)
    } else {
      history.push('/app/wallets')
    }
  }

  return (
    <div className={className}>
      <p className="text-description-subtle mb-2">{label}</p>
      <DropdownMenu
        renderList={
          walletsData && walletsData?.length > 0 ? (
            [
              ...walletsData
                .slice() // Create a copy to avoid mutating the original array
                .filter(item => {
                  // Only show compatible wallets
                  return isWalletAndCryptoCurrencyPairCompatible(item, currency)
                })
                .sort((a, b) => {
                  // Sort compatible wallets first
                  const compatibleA = isWalletAndCryptoCurrencyPairCompatible(
                    a,
                    currency
                  )
                  const compatibleB = isWalletAndCryptoCurrencyPairCompatible(
                    b,
                    currency
                  )

                  if (compatibleA && !compatibleB) return -1
                  if (!compatibleA && compatibleB) return 1
                  return 0
                })
                .map((item, idx) => {
                  const isUnknownHostingType =
                    item.hostingType === 'Unknown' || !item.hostingType
                  const isNotVerified =
                    item.screeningStatus !== 'Approved'
                  const isDisabled =
                    (isUnknownHostingType || isNotVerified) && !isAdmin(userInfo.role)

                  const disabledReason = isUnknownHostingType
                    ? 'Hosting type not declared - declare in wallet settings'
                    : isNotVerified
                      ? 'Wallet not verified - verify in wallet settings'
                      : undefined

                  return (
                    <DropdownCell
                      key={item.id}
                      title={item.name || item.address}
                      subtitle={
                        isDisabled && disabledReason
                          ? disabledReason
                          : item.name
                            ? `${item.address} • ${item.network?.name}`
                            : item.network?.name
                      }
                      onClick={() => {
                        if (!isDisabled) {
                          setWallet(item)
                        }
                      }}
                      isActive={wallet?.id === item.id}
                      imageName={item.network}
                      disabled={isDisabled}
                      customButton={
                        isDisabled ? (
                          <Tooltip label={
                            isUnknownHostingType
                              ? 'Wallet hosting type not declared. Please declare it in your wallet settings before using it for swaps.'
                              : 'Wallet not verified. Please verify it in your wallet settings before using it for swaps.'
                          }>
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2"
                            />
                          </Tooltip>
                        ) : undefined
                      }
                    />
                  )
                }),
              // Add "Add new wallet" option at the end
              ...(showAddWalletOption
                ? [
                    <DropdownCell
                      key="add-wallet"
                      title="Add new wallet"
                      subtitle="Click to add a new wallet"
                      onClick={handleAddWalletClick}
                      isActive={false}
                      icon={
                        <FontAwesomeIcon
                          icon={faPlusCircle}
                          className="text-brand-accent dark:text-white text-lg"
                        />
                      }
                    />,
                  ]
                : []),
            ]
          ) : (
            <DropdownCell
              title="Add new wallet"
              subtitle="Your account doesn't have any wallets, press to add a new one."
              onClick={handleAddWalletClick}
              isActive={false}
              icon={
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  className="text-brand-accent dark:text-white text-lg"
                />
              }
            />
          )
        }
      >
        <div
          className={`input text-start flex items-center pl-3 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Icon src={wallet?.network} size="base" />
          {wallet ? (
            <div className="flex-1">
              <p className="text-sm font-medium">
                {wallet.name || wallet.address}
              </p>
              {wallet.name && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {wallet.address} • {wallet.network?.name}
                </p>
              )}
            </div>
          ) : (
            <p className="text-description">Select a wallet</p>
          )}
          <div className="absolute right-0 top-0 bottom-0 flex items-center mr-3 text-brand-light-text-description">
            <FontAwesomeIcon icon={faCaretDown} size="sm" />
          </div>
        </div>
      </DropdownMenu>

      {/* Add Wallet Modal */}
      <AddWalletModal
        isOpen={isAddWalletModalOpen}
        onClose={() => setIsAddWalletModalOpen(false)}
        systemWallet={isAdmin(userInfo.role)}
      />
    </div>
  )
}

export default WalletSelector
